/* FIDNO Weight — pause toggles for the story page's background loops
 * ([data-fidno-video]), and the bar that unloads as the visitor scrolls through the pinned
 * stage. No GSAP: this page does not load the home page's motion stack, and
 * one rAF-throttled scroll reading is all the effect needs.
 *
 * Scroll progress p (0..1) across the track drives:
 *   --p on the rig   the bar rises as it lightens
 *   --u on a plate   0 = on the bar, 1 = off-screen; outside plate first
 *
 * With reduced motion the class is never added, so the bar stays loaded and
 * the video stays paused on its first frame.
 */

(() => {
  'use strict';

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (v) => Math.min(1, Math.max(0, v));

  function initToggle(root) {
    if (root.dataset.fidnoVideoInit) return;
    root.dataset.fidnoVideoInit = '1';
    const toggle = root.querySelector('[data-fidno-video-toggle]');
    const video = root.querySelector('video');
    if (!toggle || !video) return;

    const setPaused = (paused) => {
      if (paused) video.pause();
      else video.play().catch(() => setPaused(true));
      toggle.setAttribute('aria-pressed', String(paused));
    };

    toggle.addEventListener('click', () => setPaused(!video.paused));
    if (reduced.matches) setPaused(true);

    /* Don't decode a video nobody can see. */
    new IntersectionObserver(([entry]) => {
      if (toggle.getAttribute('aria-pressed') === 'true') return;
      if (entry.isIntersecting) video.play().catch(() => {});
      else video.pause();
    }).observe(root);
  }

  function initUnload(root) {
    if (reduced.matches) return;
    const track = root.querySelector('.fidno-weight__track');
    const rig = root.querySelector('.fidno-weight__rig');
    const plates = Array.from(root.querySelectorAll('[data-fidno-plate]'));
    const collar = root.querySelector('.fidno-weight__collar');
    if (!track || !rig || !plates.length) return;

    root.classList.add('fidno-weight--live');

    /* Each plate leaves over its own slice of the first 85% of the scroll,
       slices overlapping so the unload reads as one motion, not three. */
    const n = plates.length;
    const span = 0.85;
    const slice = span / (n * 0.7 + 0.3);

    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = track.getBoundingClientRect();
      const travel = rect.height - window.innerHeight;
      const p = travel > 0 ? clamp(-rect.top / travel) : 0;

      rig.style.setProperty('--p', p.toFixed(4));
      plates.forEach((plate, i) => {
        const start = i * slice * 0.7;
        const u = clamp((p - start) / slice);
        /* ease-in: the plate breaks loose slowly, then slides away */
        plate.style.setProperty('--u', (u * u).toFixed(4));
        if (i === 0 && collar) collar.style.setProperty('--u', (u * u).toFixed(4));
      });
    };

    const request = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request);
    update();
  }

  function init(root) {
    if (root.dataset.fidnoWeightInit) return;
    root.dataset.fidnoWeightInit = '1';
    initUnload(root);
  }

  const boot = () => {
    document.querySelectorAll('[data-fidno-video]').forEach(initToggle);
    document.querySelectorAll('[data-fidno-weight]').forEach(init);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* Theme editor: re-run when the section is re-rendered. */
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-fidno-video]').forEach(initToggle);
    event.target.querySelectorAll('[data-fidno-weight]').forEach(init);
  });
})();
